import React from 'react';
import styled from 'styled-components';
import { rgba, rem } from 'polished';
import { Checkbox as MuiCheckbox, CheckboxProps as MuiCheckboxProps } from '@material-ui/core';

export type CheckboxProps = MuiCheckboxProps;

const UncheckedIcon = styled.span`
  display: block;
  width: ${rem('18px')};
  height: ${rem('18px')};
  box-sizing: border-box;
  border: 2px solid #dadce0;
  border-radius: 2px;
  background-color: #fff;
`;

/** Misma huella 18×18 que vacío/checked; guion fino para estado indeterminado (evita saltar el header). */
const IndeterminateIcon = styled.span`
  display: block;
  width: ${rem('18px')};
  height: ${rem('18px')};
  box-sizing: border-box;
  border-radius: 2px;
  background-color: ${props => props.theme.colors.Mi['400']};
  position: relative;
  flex-shrink: 0;
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 1.5px;
    background: #fff;
    border-radius: 1px;
  }
`;

/** Misma caja 18×18 que el estado vacío (borde incluido ≈ misma presencia visual que el relleno). */
const CheckedIcon = styled.span`
  display: block;
  width: ${rem('18px')};
  height: ${rem('18px')};
  flex-shrink: 0;
  border-radius: 2px;
  overflow: hidden;
  color: ${props => props.theme.colors.Mi['400']};
  svg {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const checkSvg = (
  <svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden focusable="false">
    <rect width="18" height="18" rx="2" fill="currentColor" />
    <path
      d="M 4.25 9.35 L 7.65 12.55 L 13.85 5.55"
      fill="none"
      stroke="#fff"
      strokeWidth="1.85"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StyledCheckbox = styled(
  React.memo(({ ...props }) => (
    <MuiCheckbox
      {...props}
      classes={{ checked: 'checked', disabled: 'disabled' }}
      checkedIcon={<CheckedIcon>{checkSvg}</CheckedIcon>}
      indeterminateIcon={<IndeterminateIcon />}
      icon={<UncheckedIcon />}
    />
  ))
)`
  color: ${props => props.theme.colors.S['800']};

  &.MuiIconButton-root {
    padding: ${rem('2px')};
  }

  &.checked {
    color: ${props => props.theme.colors.Mi['400']};
  }

  &.disabled {
    color: ${props => props.theme.colors.S['500']};
  }

  &.MuiCheckbox-indeterminate {
    color: ${props => props.theme.colors.Mi['400']};
  }

  &.MuiIconButton-root:hover {
    background-color: ${props => rgba(props.theme.colors.Mi['400'], 0.1)};
  }

  &.disabled ${UncheckedIcon} {
    opacity: 0.55;
    border-color: ${props => props.theme.colors.S['400']};
  }

  &.disabled ${CheckedIcon},
  &.disabled ${IndeterminateIcon} {
    opacity: 0.55;
  }
`;

export const Checkbox = React.memo(function Checkbox({ ...props }: CheckboxProps) {
  return <StyledCheckbox {...props} />;
});
